<template>
    <page :tabs="tabs" :busy="busy">
        <grid
            ref="grid"
            :columnDefs="columnDefs"
            :getRowStyle="getRowStyle"
            :fetchCallback="fetchCallback"
            :pinnedBottomRowData="pinnedBottomRowData"
            :isAutoWidth="true"
            @on-total-records="setTotalRecords"
            gridTheme="ag-theme-alpine-report"
        >
            <date-range-picker
                :range-options="predefinedRanges"
                :value="dateRange"
                :date-format="userUnicodeDateFormat"
                @input="handleDaterangeChange"
            ></date-range-picker>
            <responsive-button :icon="['fas', 'arrows-rotate']" variant="primary" @click="refresh"
                >Refresh</responsive-button
            >
            <responsive-button
                :href="exportExcelHref"
                @click="generatingExcelReportSuccessToast"
                :icon="['fas', 'file-excel']"
                variant="primary"
            ></responsive-button>
        </grid>
    </page>
</template>

<script>
    import API from '../../api';
    import page from '../generic/page/page.vue';
    import grid from '../generic/grid/grid.vue';
    import ResponsiveButton from '../generic/responsive-button/responsive-button.vue';
    import format from 'date-fns/format';
    import { mapGetters } from 'vuex';

    import DateRangePicker, {
        ALL_DATES,
        LAST_MONTH,
        LAST_YEAR,
        nameToRange,
        THIS_MONTH,
        THIS_YEAR,
        TODAY,
        YESTERDAY,
    } from '../generic/date-range-picker/date-range-picker.vue';
    import { ADMIN_REVENUES_GRID_ENDPOINT } from '../../endpoints';

    export default {
        components: {
            page,
            grid,
            DateRangePicker,
            ResponsiveButton,
        },
        data() {
            return {
                busy: null,
                offset: 0,
                limit: 20,
                sort: 'id',
                desc: false,
                search: '',
                dateRange: nameToRange(THIS_MONTH),
                dateRequestFormat: 'yyyy-MM-dd',
                predefinedRanges: [TODAY, YESTERDAY, THIS_MONTH, LAST_MONTH, THIS_YEAR, LAST_YEAR, ALL_DATES],
                tabs: [
                    {
                        title: 'Revenues By Date',
                        route: '/app/reports/revenues/',
                        active: true,
                    },
                ],
                getRowStyle: null,
                pinnedBottomRowData: null,
                totalRecords: 0,
            };
        },
        computed: {
            ...mapGetters('header', ['userUnicodeDateFormat', 'userCurrencySymbol']),
            columnDefs() {
                return [
                    {
                        headerName: '#',
                        minWidth: 50,
                        valueGetter: (params) => {
                            return params.node.rowPinned ? this.totalRecords : params.node.rowIndex + 1;
                        },
                        sortable: false,
                    },
                    {
                        field: 'payment_date',
                        headerName: 'Billing Date',
                        type: 'dateColumn',
                        sort: 'desc',
                    },
                    {
                        field: 'company',
                        headerName: 'Company',
                        minWidth: 140,
                    },
                    {
                        field: 'email',
                        headerName: 'Email',
                        minWidth: 140,
                    },
                    {
                        field: 'notes',
                        headerName: 'Description',
                        minWidth: 140,
                    },
                    {
                        field: 'amount',
                        headerName: 'Amount',
                        headerClass: 'ag-left-aligned-header',
                        cellClass: 'ag-left-aligned-cell',
                        type: 'amountColumn',
                        minWidth: 140,
                        autoWidthExclude: true,
                    },
                ];
            },
            startDateRequest() {
                return format(this.dateRange.from, this.dateRequestFormat);
            },
            endDateRequest() {
                return format(this.dateRange.to, this.dateRequestFormat);
            },
            exportExcelHref() {
                return `/app/reports/revenues/admin-revenues/excel/?offset=${this.offset}&limit=${this.limit}&sort=${this.sort}&desc=${this.desc}&search=${this.search}&start_date=${this.startDateRequest}&end_date=${this.endDateRequest}`;
            },
            period() {
                return `Period: ${format(this.dateRange.from, this.userUnicodeDateFormat)} - ${format(
                    this.dateRange.to,
                    this.userUnicodeDateFormat
                )}`;
            },
            title() {
                return 'Revenues By Date';
            },
            fetchCallback() {
                return async ({ offset, limit, sort, desc, search }) => {
                    this.offset = offset;
                    this.limit = limit;
                    this.sort = sort;
                    this.desc = desc;
                    this.search = search;
                    const response = await API.get(ADMIN_REVENUES_GRID_ENDPOINT(), {
                        params: {
                            offset,
                            limit,
                            sort,
                            desc,
                            search,
                            start_date: this.startDateRequest,
                            end_date: this.endDateRequest,
                        },
                    });
                    this.pinnedBottomRowData = [{ amount: response.data.total_amount }];
                    return response;
                };
            },
        },
        beforeMount() {
            this.getRowStyle = (params) => {
                if (params.node.rowPinned) {
                    return { 'font-weight': 'bold' };
                }
            };
            this.pinnedBottomRowData = [{ amount: 0 }];
        },
        methods: {
            refresh() {
                this.$refs.grid.refreshDataSource();
            },
            setTotalRecords(value) {
                this.totalRecords = value;
            },
            handleDaterangeChange(value) {
                this.dateRange = { ...value };
                this.$refs.grid.refreshDataSource();
            },
        },
    };
</script>
